import { useState, useRef } from "react";
import { Bug, Shield, Phone, Mail, MapPin, Check, AlertTriangle, Home, FileSearch, Syringe, Clock, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Header } from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// SEO Component
const PageSEO = () => (
  <>
    <title>Termite Inspection & Treatment Ipswich & Brisbane | Pest Free</title>
    <meta name="description" content="Professional termite inspections and treatment in Ipswich & Brisbane. Chemical barriers, baiting systems, active treatments. QBCC Licensed. Protect your home." />
    <meta name="keywords" content="termite inspection Brisbane, termite treatment Ipswich, white ant control, termite barrier Brisbane, termite baiting system, pre-construction termite treatment Queensland" />
    <link rel="canonical" href="https://pestfreebrisbane.com.au/termites" />
    <meta property="og:title" content="Termite Inspection & Treatment Ipswich & Brisbane | Pest Free Ipswich & Brisbane" />
    <meta property="og:description" content="Professional termite inspections and treatment. Protect your biggest investment. Call (07) 3708 1422." />
    <meta property="og:url" content="https://pestfreebrisbane.com.au/termites" />
  </>
);

// Theme colors for termites - Brown/Orange
const theme = {
  primary: "#D2691E",
  primaryDark: "#B8581A",
  primaryLight: "#D2691E20",
  gradient: "from-[#D2691E] to-[#8B4513]"
};

export default function TermitesPage() {
  const bookingRef = useRef(null);
  
  const scrollToBooking = () => {
    bookingRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen">
      <PageSEO />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden" data-testid="termites-hero">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_fd293a20-78a6-4d86-9d01-01576b2818e2/artifacts/rahuvmhg_IMG_7747.jpeg" alt="" className="w-full h-full object-cover" style={{ objectPosition: '50% 30%' }} />
          <div className={`absolute inset-0 bg-gradient-to-br ${theme.gradient} opacity-75`} />
        </div>
        <div className="absolute inset-0 bg-slate-900/20" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                <Bug className="w-5 h-5" />
                <span className="font-medium">Termite Specialists</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-6 leading-tight">
                Termite Inspections & Treatment <span className="text-yellow-300">Ipswich & Brisbane</span>
              </h1>
              
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                Protect your biggest investment. Professional termite inspections, chemical barriers, and management systems for complete peace of mind.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={scrollToBooking}
                  className="group relative overflow-hidden rounded-full px-8 py-4 font-semibold text-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105"
                  style={{ background: 'linear-gradient(90deg, #7ABC32, #5fa828)' }}
                  data-testid="termites-hero-cta"
                >
                  <span className="relative flex items-center justify-center gap-2 text-white">
                    <FileSearch className="w-5 h-5" />
                    Book an Inspection
                    <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                </button>
                <a 
                  href="tel:0737081422"
                  className="group relative overflow-hidden inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-lg font-semibold transition-all duration-300 hover:scale-105 bg-transparent border-2 border-white/50 hover:border-white text-white backdrop-blur-sm"
                >
                  <Phone className="w-5 h-5" />
                  (07) 3708 1422
                </a>
              </div>
            </div>
            
            {/* Video/Image Placeholder */}
            <div className="hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-slate-900/30 aspect-video">
                <video 
                  className="w-full h-full object-cover object-bottom"
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  poster="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80"
                >
                  <source src="https://customer-assets.emergentagent.com/job_3487a350-8ab4-4ea9-a426-6bca9ba7d4b0/artifacts/borxzg8l_IMG_1679.mov" type="video/mp4" />
                </video>
                <div className="absolute bottom-4 left-4 bg-slate-900/70 backdrop-blur-sm rounded-lg px-4 py-2">
                  <p className="text-white text-sm font-medium">Live termite inspection footage</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Services Section */}
      <section className="py-20 bg-white" data-testid="termites-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-slate-900 mb-4">
              Our Termite <span style={{ color: theme.primary }}>Services</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive termite management from inspection to ongoing protection.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { 
                icon: FileSearch, 
                title: "Termite Inspections", 
                desc: "Thorough inspections using the latest technology to detect termite activity and damage." 
              },
              { 
                icon: Syringe, 
                title: "Active Treatments", 
                desc: "Direct treatment of active termite infestations to eliminate colonies fast." 
              },
              { 
                icon: Shield, 
                title: "Chemical Barriers", 
                desc: "Professional soil treatment creating a protective barrier around your property." 
              },
              { 
                icon: Home, 
                title: "Baiting Systems", 
                desc: "Strategic bait stations to eliminate entire termite colonies." 
              },
              { 
                icon: Clock, 
                title: "Management Plans", 
                desc: "Ongoing protection programs tailored to your property's needs." 
              }
            ].map((service, i) => (
              <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-[#D2691E]/30 hover:shadow-lg transition-all">
                <div className="inline-flex p-3 rounded-xl mb-4" style={{ backgroundColor: theme.primaryLight }}>
                  <service.icon className="w-8 h-8" style={{ color: theme.primary }} />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{service.title}</h3>
                <p className="text-slate-600">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Warning Signs Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Warning Signs of <span className="text-[#D2691E]">Termite Damage</span>
            </h2>
            <p className="text-lg text-slate-400">Don't ignore these signs — call us immediately</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Hollow-sounding timber when tapped",
              "Mud tubes on walls or foundations",
              "Bubbling or cracked paint",
              "Tight-fitting doors or windows",
              "Discarded termite wings",
              "Visible termite droppings (frass)"
            ].map((sign, i) => (
              <div key={i} className="flex items-start gap-4 bg-white/5 rounded-xl p-5 border border-white/10">
                <div className="p-2 bg-[#D2691E]/20 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-[#D2691E]" />
                </div>
                <p className="text-slate-200 font-medium">{sign}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-slate-400 mb-4">Termites cause over $1.5 billion in damage to Australian homes each year.</p>
            <Button 
              onClick={scrollToBooking}
              className="bg-[#D2691E] hover:bg-[#B8581A] text-white px-8 py-6 rounded-full text-lg font-semibold"
            >
              Book an Inspection
            </Button>
          </div>
        </div>
      </section>
      
      {/* Gallery Placeholder */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              Our Work in <span style={{ color: theme.primary }}>Action</span>
            </h2>
            <p className="text-lg text-slate-600">Professional termite treatment across Ipswich & Brisbane</p>
          </div>
          
          {/* Placeholder for gallery - will be updated with real photos */}
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="aspect-video bg-slate-100 rounded-2xl flex items-center justify-center border-2 border-dashed border-slate-300">
                <div className="text-center text-slate-400">
                  <Bug className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Photos coming soon</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Contact Form Section */}
      <section ref={bookingRef} id="contact" className="py-20 bg-slate-50" data-testid="termites-contact">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-slate-900 mb-4">
              Get a <span style={{ color: theme.primary }}>Free Quote</span>
            </h2>
            <p className="text-lg text-slate-600">
              Tell us about your property and we'll provide a tailored solution.
            </p>
          </div>
          
          <ContactForm serviceType="termites" theme={theme} />
        </div>
      </section>
      
      {/* Service Areas - Grouped Regional Coverage */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-slate-900">
              Termite Services Across <span style={{ color: theme.primary }}>Ipswich & Brisbane</span>
            </h2>
            <p className="text-lg text-slate-600 mt-2">Professional termite protection across South East Queensland</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                name: "Ipswich Region",
                suburbs: "Ipswich CBD, Brassall, Booval, Bundamba, Redbank Plains, Goodna, Springfield, Springfield Lakes, Ripley, Deebing Heights, Yamanto, Rosewood"
              },
              {
                name: "Lockyer Valley",
                suburbs: "Gatton, Laidley, Plainland, Withcott, Helidon, Forest Hill, Grantham"
              },
              {
                name: "Brisbane South",
                suburbs: "Mt Gravatt, Sunnybank, Carindale, Wishart, Upper Mt Gravatt, Eight Mile Plains, Rochedale, Springwood"
              },
              {
                name: "Brisbane West",
                suburbs: "Indooroopilly, Kenmore, Chapel Hill, Jindalee, Mt Ommaney, Centenary, Sherwood, Graceville, Taringa"
              },
              {
                name: "Logan Corridor",
                suburbs: "Logan Central, Woodridge, Browns Plains, Marsden, Crestmead, Beenleigh, Eagleby, Waterford"
              }
            ].map((region, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-5 border border-slate-200 hover:border-[#D2691E]/30 hover:shadow-md transition-all">
                <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4" style={{ color: theme.primary }} />
                  {region.name}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">{region.suburbs}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

// Contact Form Component
function ContactForm({ serviceType, theme }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    suburb: "",
    propertyType: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        serviceType,
        timestamp: new Date().toISOString(),
        pageUrl: window.location.href
      });
      setSubmitted(true);
      toast.success("Message sent! We'll contact you within 2 hours.");
    } catch (error) {
      console.error(error);
      toast.error("Failed to send message. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
        <Check className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-2xl font-heading font-bold text-slate-900 mb-2">Message Received!</h3>
        <p className="text-lg text-slate-600 mb-6">
          Thanks! We'll contact you within 2 hours to discuss your needs.
        </p>
        <a 
          href="tel:0737081422"
          className="inline-flex items-center gap-2 text-lg font-semibold hover:underline"
          style={{ color: theme.primary }}
        >
          <Phone className="w-5 h-5" />
          Or call now: (07) 3708 1422
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 md:p-8 border border-slate-200 shadow-sm" data-testid="contact-form">
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-2 block">Full Name *</Label>
          <Input
            required
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            className="w-full"
            placeholder="Your name"
            data-testid="contact-name"
          />
        </div>
        
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-2 block">Phone *</Label>
          <Input
            required
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            className="w-full"
            placeholder="0400 000 000"
            data-testid="contact-phone"
          />
        </div>
        
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-2 block">Email *</Label>
          <Input
            required
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            className="w-full"
            placeholder="your@email.com"
            data-testid="contact-email"
          />
        </div>
        
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-2 block">Suburb *</Label>
          <Input
            required
            value={formData.suburb}
            onChange={(e) => setFormData({...formData, suburb: e.target.value})}
            className="w-full"
            placeholder="e.g., Brisbane, Ipswich"
            data-testid="contact-suburb"
          />
        </div>
        
        <div className="md:col-span-2">
          <Label className="text-sm font-medium text-slate-700 mb-2 block">Property Address</Label>
          <Input
            value={formData.address}
            onChange={(e) => setFormData({...formData, address: e.target.value})}
            className="w-full"
            placeholder="Street address (optional)"
            data-testid="contact-address"
          />
        </div>
        
        <div className="md:col-span-2">
          <Label className="text-sm font-medium text-slate-700 mb-2 block">Property Type</Label>
          <RadioGroup 
            value={formData.propertyType} 
            onValueChange={(v) => setFormData({...formData, propertyType: v})}
            className="flex flex-wrap gap-4"
          >
            {["House", "Townhouse", "Unit/Apartment", "Commercial"].map((type) => (
              <div key={type} className="flex items-center space-x-2">
                <RadioGroupItem value={type.toLowerCase()} id={type.toLowerCase()} />
                <Label htmlFor={type.toLowerCase()} className="cursor-pointer">{type}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>
        
        <div className="md:col-span-2">
          <Label className="text-sm font-medium text-slate-700 mb-2 block">Tell us about your situation</Label>
          <Textarea
            value={formData.message}
            onChange={(e) => setFormData({...formData, message: e.target.value})}
            className="w-full min-h-[120px]"
            placeholder="Describe what you're experiencing or what service you need..."
            data-testid="contact-message"
          />
        </div>
      </div>
      
      <div className="mt-6 flex flex-col sm:flex-row gap-4">
        <Button
          type="submit"
          disabled={submitting}
          className="flex-1 text-lg py-6 rounded-full text-white"
          style={{ backgroundColor: theme.primary }}
          data-testid="contact-submit"
        >
          {submitting ? "Sending..." : "Send Message"}
        </Button>
        <a 
          href="tel:0737081422"
          className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-lg font-semibold border-2 transition-all"
          style={{ borderColor: theme.primary, color: theme.primary }}
        >
          <Phone className="w-5 h-5" />
          Call Instead
        </a>
      </div>
    </form>
  );
}
